import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  MenuItem,
  InputAdornment,
  IconButton,
  Typography as MuiTypography,
  Autocomplete,
  TextField,
  Chip,
  Divider,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { 
  Visibility, 
  VisibilityOff,
  Api as ApiIcon,
  Help as HelpIcon
} from '@mui/icons-material';

// ----- Komponen custom -----
import CustomFormLabel from '../../../forms/theme-elements/CustomFormLabel';
import CustomTextField from '../../../forms/theme-elements/CustomTextField';
import CustomSelect from '../../../forms/theme-elements/CustomSelect';
import RegisterTooltip from 'src/components/forms/RegisterTooltip';

// ----- Hook & Utils -----
import { useFormHandler } from '../../../../utils/useFormHandler';
import { emailScheduleValidationSchema } from '../../../../utils/validationSchemas';
import {
  registerEmailSchedule,
  updateEmailSchedule,
  fetchEmailScheduleById,
} from '../../../../services/EmailScheduleService';
import { fetchEmailTemplatesFromAPI } from '../../../../services/EmailTemplateService';
import { fetchEmailRecipientsFromAPI } from '../../../../services/EmailRecipientsService';
import withRegisterFormTooltips from 'src/hoc/withRegisterFormTooltips';
import { getRegisterFormTooltipByKey } from 'src/constants/registerFormTooltips';

const EmailScheduleRegisterForm = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const emailScheduleId = state?.emailScheduleId;

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  // Email recipients state
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Snackbar states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // API Configuration toggle
  const [enableApiConfig, setEnableApiConfig] = useState(false);

  // Available options for multiple select
  const dayOptions = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const dateOptions = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: `Date ${i + 1}`
  }));

  // Fetch email templates on component mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const response = await fetchEmailTemplatesFromAPI(1, 100, '', {});
        setEmailTemplates(response.data.emailTemplates || []);
      } catch (error) {
        console.error('Error fetching email templates:', error);
        setEmailTemplates([]);
      } finally {
        setTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Fetch email recipients on component mount
  useEffect(() => {
    const fetchRecipients = async () => {
      setRecipientsLoading(true);
      try {
        const response = await fetchEmailRecipientsFromAPI(1, 1000, '', {});
        setEmailRecipients(response?.data?.emailRecipients || []);
      } catch (error) {
        console.error('Error fetching email recipients:', error);
        setEmailRecipients([]);
      } finally {
        setRecipientsLoading(false);
      }
    };

    fetchRecipients();
  }, []);

  const apiFunction = emailScheduleId ? updateEmailSchedule : registerEmailSchedule;

  const onSuccess = () => {
    setSnackbarMessage(emailScheduleId ? 'Email schedule updated successfully!' : 'Email schedule created successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);

    setTimeout(() => {
      navigate('/managements/email-schedules');
    }, 1000);
  };

  const onError = (error) => {
    setSnackbarMessage(error || (emailScheduleId ? 'Update failed! Please try again.' : 'Creation failed! Please try again.'));
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  };

  const formik = useFormHandler(
    {
      schedule_name: '',
      schedule_type: '',
      schedule_time: '',
      schedule_days: '',
      schedule_dates: '',
      template_id: '',
      is_active: true,
      description: '',
      api_url: '',
      api_method: 'GET',
      api_params: {},
      api_headers: {},
      recipient_id: '',
    },
    emailScheduleValidationSchema,
    apiFunction,
    onSuccess,
    onError,
    emailScheduleId
  );


  // Store schedule data temporarily
  const [scheduleData, setScheduleData] = useState(null);

  // Fetch schedule data when in edit mode
  useEffect(() => {
    if (emailScheduleId) {
      fetchEmailScheduleById(emailScheduleId)
        .then((response) => {
          const res = response.data.emailSchedule;
          setScheduleData(res);
          const hasApiConfig = !!(res.api_url);
          setEnableApiConfig(hasApiConfig);
        })
        .catch(() => {
          setSnackbarMessage('Failed to fetch email schedule data.');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        });
    }
  }, [emailScheduleId]);

  // Set form values when both schedule data and email templates are loaded
  useEffect(() => {
    if (scheduleData && emailTemplates.length > 0) {
      // Convert template_id to string for matching (in case backend returns number)
      const templateIdStr = String(scheduleData.template_id || '');
      
      formik.setValues({
        schedule_name: scheduleData.schedule_name || '',
        schedule_type: scheduleData.schedule_type || '',
        schedule_time: scheduleData.schedule_time || '',
        schedule_days: scheduleData.schedule_days || '',
        schedule_dates: scheduleData.schedule_dates || '',
        template_id: templateIdStr,
        is_active: scheduleData.is_active !== undefined ? scheduleData.is_active : true,
        description: scheduleData.description || '',
        api_url: scheduleData.api_url || '',
        api_method: scheduleData.api_method || 'GET',
        api_params: scheduleData.api_params || {},
        api_headers: scheduleData.api_headers || {},
        recipient_id: scheduleData.recipient_id || '',
      });
    }
  }, [scheduleData, emailTemplates]);

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
        {emailScheduleId ? 'Edit Email Schedule' : 'Create Email Schedule'}
      </Typography>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <form 
        onSubmit={(e) => {
          e.preventDefault();
          // Clear API fields if checkbox is not checked
          if (!enableApiConfig) {
            formik.setFieldValue('api_url', '');
            formik.setFieldValue('api_method', 'GET');
            formik.setFieldValue('api_params', {});
            formik.setFieldValue('api_headers', {});
          }
          formik.handleSubmit(e);
        }} 
        noValidate
      >
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Email Schedule Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Grid>

          {/* Schedule Name */}
          <Grid item xs={12} md={6}>
            <CustomFormLabel htmlFor="schedule_name">Schedule Name *</CustomFormLabel>
              <CustomTextField
                id="schedule_name"
                fullWidth
                placeholder="Enter schedule name"
              tooltipKey="emailSchedule.schedule_name"
                {...formik.getFieldProps('schedule_name')}
                error={formik.touched.schedule_name && Boolean(formik.errors.schedule_name)}
                helperText={formik.touched.schedule_name && formik.errors.schedule_name}
              />
          </Grid>

          {/* Schedule Type */}
          <Grid item xs={12} md={6}>
            <CustomFormLabel htmlFor="schedule_type">Schedule Type *</CustomFormLabel>
              <CustomSelect
                id="schedule_type"
                fullWidth
              tooltipKey="emailSchedule.schedule_type"
                {...formik.getFieldProps('schedule_type')}
                error={formik.touched.schedule_type && Boolean(formik.errors.schedule_type)}
              >
                <MenuItem value="minute">Every Minute</MenuItem>
                <MenuItem value="hourly">Every Hour</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </CustomSelect>
            {formik.touched.schedule_type && formik.errors.schedule_type && (
              <MuiTypography color="error" variant="body2" mt={0.5}>
                {formik.errors.schedule_type}
              </MuiTypography>
            )}
          </Grid>

          {/* Schedule Time - Conditional based on type */}
          {formik.values.schedule_type === 'minute' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_time">Every X Minutes</CustomFormLabel>
                <CustomTextField
                  id="schedule_time"
                  fullWidth
                  type="number"
                  placeholder="e.g., 5 (every 5 minutes)"
                tooltipKey="emailSchedule.every_minutes"
                  {...formik.getFieldProps('schedule_time')}
                  error={formik.touched.schedule_time && Boolean(formik.errors.schedule_time)}
                  helperText={formik.touched.schedule_time && formik.errors.schedule_time}
                />
            </Grid>
          )}

          {formik.values.schedule_type === 'hourly' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_time">Every X Hours</CustomFormLabel>
                <CustomTextField
                  id="schedule_time"
                  fullWidth
                  type="number"
                  placeholder="e.g., 2 (every 2 hours)"
                tooltipKey="emailSchedule.every_hours"
                  {...formik.getFieldProps('schedule_time')}
                  error={formik.touched.schedule_time && Boolean(formik.errors.schedule_time)}
                  helperText={formik.touched.schedule_time && formik.errors.schedule_time}
                />
            </Grid>
          )}

          {(formik.values.schedule_type === 'daily' || formik.values.schedule_type === 'weekly' || formik.values.schedule_type === 'monthly') && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_time">Time of Day</CustomFormLabel>
                <CustomTextField
                  id="schedule_time"
                  fullWidth
                  type="time"
                tooltipKey="emailSchedule.time_of_day"
                  {...formik.getFieldProps('schedule_time')}
                  error={formik.touched.schedule_time && Boolean(formik.errors.schedule_time)}
                  helperText={formik.touched.schedule_time && formik.errors.schedule_time}
                />
            </Grid>
          )}

          {formik.values.schedule_type === 'custom' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_time">Custom Schedule (Cron Expression)</CustomFormLabel>
                <CustomTextField
                  id="schedule_time"
                  fullWidth
                  placeholder="e.g., 0 9 * * 1-5 (9 AM, Monday to Friday)"
                tooltipKey="emailSchedule.cron"
                  {...formik.getFieldProps('schedule_time')}
                  error={formik.touched.schedule_time && Boolean(formik.errors.schedule_time)}
                  helperText="Use cron expression format: minute hour day month weekday"
                />
            </Grid>
          )}

          {/* Template ID */}
          <Grid item xs={12} md={6}>
            <CustomFormLabel htmlFor="template_id">Email Template *</CustomFormLabel>
            <RegisterTooltip tooltipKey="emailSchedule.template_id">
              <Autocomplete
                id="template_id"
                options={emailTemplates || []}
                getOptionLabel={(option) => `${option.template_name} (${option.template_type})`}
                value={emailTemplates?.find(template => String(template.id) === String(formik.values.template_id)) || null}
                onChange={(event, newValue) => {
                  formik.setFieldValue('template_id', newValue?.id || '');
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                loading={templatesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select email template"
                    error={formik.touched.template_id && Boolean(formik.errors.template_id)}
                    helperText={formik.touched.template_id && formik.errors.template_id}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.template_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {option.template_type} | Subject: {option.subject}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </RegisterTooltip>
          </Grid>

          {/* Recipient ID */}
          <Grid item xs={12} md={6}>
            <CustomFormLabel htmlFor="recipient_id">Email Recipient *</CustomFormLabel>
            <RegisterTooltip tooltipKey="emailSchedule.recipient_id">
              <Autocomplete
                id="recipient_id"
                options={emailRecipients || []}
                getOptionLabel={(option) => `${option.recipient_name} (${option.email_address})`}
                value={emailRecipients?.find(recipient => recipient.id === formik.values.recipient_id) || null}
                onChange={(event, newValue) => {
                  formik.setFieldValue('recipient_id', newValue?.id || '');
                }}
                loading={recipientsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select email recipient"
                    error={formik.touched.recipient_id && Boolean(formik.errors.recipient_id)}
                    helperText={
                      (formik.touched.recipient_id && formik.errors.recipient_id) ||
                      'Each recipient can have one schedule (one-to-one relationship)'
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {option.recipient_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.email_address} {option.is_active ? '(Active)' : '(Inactive)'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </RegisterTooltip>
          </Grid>

          {/* Schedule Days - Only show for weekly */}
          {formik.values.schedule_type === 'weekly' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_days">Select Days of Week</CustomFormLabel>
              <RegisterTooltip tooltipKey="emailSchedule.schedule_days_weekly">
              <Autocomplete
                multiple
                id="schedule_days"
                options={dayOptions}
                getOptionLabel={(option) => option.label}
                value={dayOptions.filter(option => 
                  formik.values.schedule_days && 
                  formik.values.schedule_days.split(', ').includes(option.value)
                )}
                onChange={(event, newValue) => {
                  const selectedValues = newValue.map(item => item.value);
                  formik.setFieldValue('schedule_days', selectedValues.join(', '));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select days"
                    error={formik.touched.schedule_days && Boolean(formik.errors.schedule_days)}
                    helperText={formik.touched.schedule_days && formik.errors.schedule_days}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
              />
              </RegisterTooltip>
            </Grid>
          )}

          {/* Schedule Dates - Only show for monthly */}
          {formik.values.schedule_type === 'monthly' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_dates">Select Dates in Month (1-31)</CustomFormLabel>
              <RegisterTooltip tooltipKey="emailSchedule.schedule_dates_monthly">
              <Autocomplete
                multiple
                id="schedule_dates"
                options={dateOptions}
                getOptionLabel={(option) => option.label}
                value={dateOptions.filter(option => 
                  formik.values.schedule_dates && 
                  formik.values.schedule_dates.split(', ').includes(option.value.toString())
                )}
                onChange={(event, newValue) => {
                  const selectedValues = newValue.map(item => item.value);
                  formik.setFieldValue('schedule_dates', selectedValues.join(', '));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select days of month"
                    error={formik.touched.schedule_dates && Boolean(formik.errors.schedule_dates)}
                    helperText={formik.touched.schedule_dates && formik.errors.schedule_dates}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
              />
              </RegisterTooltip>
            </Grid>
          )}

          {/* Schedule Days - For custom cron */}
          {formik.values.schedule_type === 'custom' && (
            <Grid item xs={12} md={6}>
              <CustomFormLabel htmlFor="schedule_days">Days of Week (Optional)</CustomFormLabel>
              <RegisterTooltip tooltipKey="emailSchedule.schedule_days_custom">
              <Autocomplete
                multiple
                id="schedule_days"
                options={dayOptions}
                getOptionLabel={(option) => option.label}
                value={dayOptions.filter(option => 
                  formik.values.schedule_days && 
                  formik.values.schedule_days.split(', ').includes(option.value)
                )}
                onChange={(event, newValue) => {
                  const selectedValues = newValue.map(item => item.value);
                  formik.setFieldValue('schedule_days', selectedValues.join(', '));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select days (optional)"
                    error={formik.touched.schedule_days && Boolean(formik.errors.schedule_days)}
                    helperText="Leave empty if using cron expression"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.value}
                    />
                  ))
                }
              />
              </RegisterTooltip>
            </Grid>
          )}

          {/* Status */}
          <Grid item xs={12} md={6}>
            <CustomFormLabel htmlFor="is_active">Status</CustomFormLabel>
            <CustomSelect
              id="is_active"
              fullWidth
              tooltipKey="emailSchedule.status"
              {...formik.getFieldProps('is_active')}
              error={formik.touched.is_active && Boolean(formik.errors.is_active)}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </CustomSelect>
            {formik.touched.is_active && formik.errors.is_active && (
              <MuiTypography color="error" variant="body2" mt={0.5}>
                {formik.errors.is_active}
              </MuiTypography>
            )}
          </Grid>

          {/* Schedule Type Helper */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Schedule Type Explanation:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formik.values.schedule_type === 'minute' && "Every X minutes: Set how many minutes between each execution (e.g., 5 = every 5 minutes)"}
                {formik.values.schedule_type === 'hourly' && "Every X hours: Set how many hours between each execution (e.g., 2 = every 2 hours)"}
                {formik.values.schedule_type === 'daily' && "Daily: Runs once per day at the specified time"}
                {formik.values.schedule_type === 'weekly' && "Weekly: Runs on selected days of the week at the specified time"}
                {formik.values.schedule_type === 'monthly' && "Monthly: Runs on selected dates of the month at the specified time"}
                {formik.values.schedule_type === 'custom' && "Custom: Use cron expression for advanced scheduling (e.g., '0 9 * * 1-5' = 9 AM, Monday to Friday)"}
              </Typography>
            </Box>
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <CustomFormLabel htmlFor="description">Description</CustomFormLabel>
            <CustomTextField
              id="description"
              fullWidth
              multiline
              rows={4}
              placeholder="Enter description for this email schedule"
              tooltipKey="emailSchedule.description"
              {...formik.getFieldProps('description')}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>

          {/* API Configuration Toggle */}
          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableApiConfig}
                  onChange={(e) => {
                    setEnableApiConfig(e.target.checked);
                    // Clear API fields when unchecked
                    if (!e.target.checked) {
                      formik.setFieldValue('api_url', '');
                      formik.setFieldValue('api_method', 'GET');
                      formik.setFieldValue('api_params', {});
                      formik.setFieldValue('api_headers', {});
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ApiIcon color="primary" />
                  <Typography variant="h6">
                    Enable API Configuration
                  </Typography>
                  <Tooltip
                    title={getRegisterFormTooltipByKey(
                      'emailSchedule.api_toggle_info',
                      'Configure API to fetch data before sending email. Data from API will be merged into template variables.'
                    )}
                  >
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            {enableApiConfig && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 4 }}>
                The system will call this API before sending email and merge the response data into template variables.
              </Typography>
            )}
          </Grid>

          {/* API Configuration Section - Only show if checkbox is checked */}
          {enableApiConfig && (
            <>
              {/* API URL */}
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="api_url">API URL *</CustomFormLabel>
                <CustomTextField
                  id="api_url"
                  fullWidth
                  placeholder="https://api.example.com/data"
                  tooltipKey="emailSchedule.api_url"
                  {...formik.getFieldProps('api_url')}
                  error={formik.touched.api_url && Boolean(formik.errors.api_url)}
                  helperText={formik.touched.api_url && formik.errors.api_url || "Full URL of the API endpoint to fetch data"}
                />
              </Grid>

              {/* API Method */}
              <Grid item xs={12} md={6}>
                <CustomFormLabel htmlFor="api_method">HTTP Method</CustomFormLabel>
                <CustomSelect
                  id="api_method"
                  fullWidth
                  tooltipKey="emailSchedule.api_method"
                  {...formik.getFieldProps('api_method')}
                  error={formik.touched.api_method && Boolean(formik.errors.api_method)}
                  helperText={formik.touched.api_method && formik.errors.api_method}
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </CustomSelect>
              </Grid>

              {/* API Params */}
              <Grid item xs={12} md={6}>
                <CustomFormLabel htmlFor="api_params">API Parameters (JSON)</CustomFormLabel>
                <CustomTextField
                  id="api_params"
                  fullWidth
                  multiline
                  rows={4}
                  placeholder='{"key": "value", "month": "current"}'
                  tooltipKey="emailSchedule.api_params"
                  value={typeof formik.values.api_params === 'object' ? JSON.stringify(formik.values.api_params, null, 2) : formik.values.api_params || ''}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      formik.setFieldValue('api_params', parsed);
                    } catch {
                      // Invalid JSON, keep as string for now
                      formik.setFieldValue('api_params', e.target.value);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('api_params', true)}
                  error={formik.touched.api_params && Boolean(formik.errors.api_params)}
                  helperText={formik.touched.api_params && formik.errors.api_params || "For GET: query parameters. For POST/PUT: request body"}
                />
              </Grid>

              {/* API Headers */}
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="api_headers">API Headers (JSON)</CustomFormLabel>
                <CustomTextField
                  id="api_headers"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                  tooltipKey="emailSchedule.api_headers"
                  value={typeof formik.values.api_headers === 'object' ? JSON.stringify(formik.values.api_headers, null, 2) : formik.values.api_headers || ''}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      formik.setFieldValue('api_headers', parsed);
                    } catch {
                      // Invalid JSON, keep as string for now
                      formik.setFieldValue('api_headers', e.target.value);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched('api_headers', true)}
                  error={formik.touched.api_headers && Boolean(formik.errors.api_headers)}
                  helperText={formik.touched.api_headers && formik.errors.api_headers || "HTTP headers as JSON object (e.g., Authorization, Content-Type)"}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Box mt={3}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Submitting...' : emailScheduleId ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default withRegisterFormTooltips(EmailScheduleRegisterForm, 'Isi data sesuai kebutuhan field ini.');
